"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import Icon from "../../../components/Icon";
import { ApiError, api, hasSession, isAuthError } from "../../../lib/api";

interface Order {
  id: string; code: string; status: string; delivery: string;
  totalGs: number; priceListName?: string | null; createdAt: string;
  items: { qty: number; priceGs: number; product: { name: string } }[];
}

const gs = (n: number) => `₲ ${n.toLocaleString("es-PY")}`;
const ESTADO: Record<string, { label: string; color: string }> = {
  PENDING: { label: "Pendiente", color: "#E8A23C" },
  CONFIRMED: { label: "Confirmado", color: "#72A9D8" },
  READY: { label: "Listo para retirar", color: "#5CBE8F" },
  DELIVERED: { label: "Entregado", color: "#5CBE8F" },
  CANCELLED: { label: "Cancelado", color: "#DE705C" },
};

// Volver acá después de ingresar: la ruta es fija y nuestra, no hay nada que validar
const INGRESAR = `/ingresar?next=${encodeURIComponent("/tienda/pedidos")}`;

export default function MisPedidos() {
  const router = useRouter();
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    if (!hasSession()) { router.push(INGRESAR); return; }
    setError("");
    try {
      setOrders(await api<Order[]>("/store/orders"));
    } catch (e) {
      // Antes cualquier error mandaba al login: un 500 o un corte de red
      // expulsaba a alguien que tenía la sesión perfecta.
      if (isAuthError(e)) { router.push(INGRESAR); return; }
      setError(e instanceof ApiError ? e.message : "No pudimos conectarnos. Revisá tu conexión y probá de nuevo.");
      setOrders([]);
    }
  }, [router]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] px-4 py-6 sm:p-6 pb-24">
      <div className="max-w-2xl mx-auto flex flex-col gap-4">
        <header className="flex items-center gap-3">
          <Link href="/tienda" aria-label="Volver a la tienda"
            className="w-11 h-11 flex-none rounded-full border border-white/15 flex items-center justify-center text-[#B5C0BB] hover:bg-white/5">
            <Icon name="chevron-left" size={20} />
          </Link>
          <h1 className="text-2xl font-bold tracking-tight">Mis pedidos</h1>
        </header>

        {error && (
          <div className="rounded-xl bg-[#DE705C]/10 border border-[#DE705C]/25 px-4 py-3 flex flex-wrap items-center gap-3">
            <Icon name="alert" size={18} className="text-[#DE705C] flex-none" />
            <span className="text-[13.5px] text-[#DE705C] flex-1 min-w-[12rem]">{error}</span>
            <button onClick={cargar} className="min-h-[44px] px-3 text-sm font-bold text-[#E8A23C]">Reintentar</button>
          </div>
        )}

        {orders === null ? (
          <div className="flex flex-col gap-3" aria-busy="true" aria-label="Cargando tus pedidos">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="rounded-xl bg-[#1A2123] border border-white/10 p-4 space-y-3">
                <div className="h-3 w-24 bg-white/6 rounded animate-pulse" />
                <div className="h-4 w-3/4 bg-white/6 rounded animate-pulse" />
                <div className="h-4 w-1/2 bg-white/6 rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          !error && (
            <div className="rounded-xl bg-[#1A2123] border border-white/10 p-8 text-center">
              <Icon name="package" size={40} className="text-white/15 mx-auto" strokeWidth={1.25} />
              <p className="mt-4 text-[14px] text-[#B5C0BB]">Todavía no hiciste pedidos.</p>
              <Link href="/tienda"
                className="mt-4 inline-flex min-h-[44px] items-center px-5 rounded-xl bg-[#E8A23C] text-[#201404] text-sm font-bold hover:brightness-105">
                Ir a la tienda
              </Link>
            </div>
          )
        ) : (
          <div className="flex flex-col gap-3">
            {orders.map((o) => {
              const est = ESTADO[o.status] ?? { label: o.status, color: "#808D88" };
              return (
                <div key={o.id} className="rounded-xl bg-[#1A2123] border border-white/10 p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <span className="font-mono text-[13px] text-[#808D88]">{o.code}</span>
                      <div className="text-[13px] text-[#808D88]">
                        {new Date(o.createdAt).toLocaleDateString("es-PY")} ·{" "}
                        <span className="inline-flex items-center gap-1.5 align-middle">
                          <Icon name={o.delivery === "PICKUP" ? "store" : "truck"} size={15} />
                          {o.delivery === "PICKUP" ? "retiro" : "envío"}
                        </span>
                        {o.priceListName && <> · {o.priceListName}</>}
                      </div>
                    </div>
                    <span className="text-2xs font-semibold rounded-full px-2.5 py-1 flex-none"
                      style={{ background: `${est.color}22`, color: est.color }}>{est.label}</span>
                  </div>
                  <div className="border-t border-white/5 pt-2">
                    {o.items.map((it, i) => (
                      <div key={i} className="flex justify-between gap-3 text-[13.5px] py-0.5">
                        <span className="min-w-0">{it.qty}× {it.product.name}</span>
                        <span className="font-mono text-[#B5C0BB] flex-none">{gs(it.priceGs * it.qty)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-bold mt-2 pt-2 border-t border-white/5">
                      <span className="text-sm">Total</span>
                      <span className="font-mono">{gs(o.totalGs)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
